/**
 * TeamChatTab - Internal team messaging between practice members
 *
 * Features:
 * - 1:1 direct messages between team members
 * - Group conversations
 * - Real-time messages with typing indicators
 * - Unread badges
 * - New conversation modal with team member selection
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, ArrowLeft, Users, Plus, X, Search, Check, CheckCheck, Send, Hash, User } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  firebaseTeamMessagingService,
  TeamConversation,
  TeamMessage
} from '../../../services/firebase/FirebaseTeamMessagingService';
import TeamMemberService, { TeamMember } from '../../../services/TeamMemberService';

const TeamChatTab: React.FC = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<TeamConversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<TeamConversation | null>(null);
  const [messages, setMessages] = useState<TeamMessage[]>([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [typingUsers, setTypingUsers] = useState<Array<{ userId: string; userName: string }>>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesUnsubRef = useRef<(() => void) | null>(null);
  const typingUnsubRef = useRef<(() => void) | null>(null);

  const currentUserId = user?.uid || '';

  // Responsive
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Subscribe to conversations
  useEffect(() => {
    if (!currentUserId) return;
    setLoading(true);
    const unsub = firebaseTeamMessagingService.subscribeToConversations(currentUserId, (convos) => {
      setConversations(convos);
      setLoading(false);
    });
    return () => unsub();
  }, [currentUserId]);

  // Subscribe to messages when active conversation changes
  useEffect(() => {
    if (messagesUnsubRef.current) messagesUnsubRef.current();
    if (typingUnsubRef.current) typingUnsubRef.current();

    if (!activeConversation || !currentUserId) {
      setMessages([]);
      setTypingUsers([]);
      return;
    }

    messagesUnsubRef.current = firebaseTeamMessagingService.subscribeToMessages(
      activeConversation.id,
      (msgs) => {
        setMessages(msgs);
        // Mark as read
        if (msgs.length > 0) {
          firebaseTeamMessagingService.markAsRead(activeConversation.id, currentUserId);
        }
      }
    );

    typingUnsubRef.current = firebaseTeamMessagingService.subscribeToTyping(
      activeConversation.id,
      currentUserId,
      setTypingUsers
    );

    return () => {
      if (messagesUnsubRef.current) messagesUnsubRef.current();
      if (typingUnsubRef.current) typingUnsubRef.current();
    };
  }, [activeConversation?.id, currentUserId]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typingUsers]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [messageText]);

  const getUserDisplayName = useCallback(() => {
    const members = TeamMemberService.getInstance().getAllTeamMembers();
    const me = members.find(m => m.authUid === currentUserId);
    return me?.name || user?.email || 'Unknown';
  }, [currentUserId, user]);

  const getUserRole = useCallback(() => {
    const members = TeamMemberService.getInstance().getAllTeamMembers();
    const me = members.find(m => m.authUid === currentUserId);
    return me?.role || 'provider';
  }, [currentUserId]);

  // Send message
  const handleSend = async () => {
    if (!messageText.trim() || !activeConversation || !currentUserId) return;
    const content = messageText.trim();
    setMessageText('');

    try {
      await firebaseTeamMessagingService.sendMessage(
        activeConversation.id,
        currentUserId,
        getUserDisplayName(),
        getUserRole(),
        content
      );
      // Stop typing
      firebaseTeamMessagingService.setTyping(activeConversation.id, currentUserId, getUserDisplayName(), false);
    } catch (error) {
      console.error('Failed to send team message:', error);
    }
  };

  // Typing indicator
  const handleTyping = () => {
    if (!activeConversation || !currentUserId) return;
    firebaseTeamMessagingService.setTyping(activeConversation.id, currentUserId, getUserDisplayName(), true);

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      if (activeConversation) {
        firebaseTeamMessagingService.setTyping(activeConversation.id, currentUserId, getUserDisplayName(), false);
      }
    }, 2000);
  };

  // Key handler for send on Enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Get display name for a conversation
  const getConversationDisplayName = (convo: TeamConversation): string => {
    if (convo.type === 'group') return convo.name || 'Group Chat';
    // For direct, show the other person's name
    const otherUid = convo.members.find(uid => uid !== currentUserId);
    return otherUid ? (convo.memberNames[otherUid] || 'Team Member') : convo.name;
  };

  // Get initials for avatar
  const getInitials = (name: string): string => {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
  };

  // Format time
  const formatTime = (date: Date): string => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const formatMessageTime = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Role badge color
  const getRoleBadgeColor = (role: string): string => {
    switch (role) {
      case 'system_admin': return '#DC2626';
      case 'clinical_director': return '#7C3AED';
      case 'provider': return '#059669';
      case 'administrative_staff': return '#2563EB';
      default: return '#6B7280';
    }
  };

  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'system_admin': return 'Admin';
      case 'clinical_director': return 'Director';
      case 'provider': return 'Provider';
      case 'administrative_staff': return 'Staff';
      default: return role;
    }
  };

  // ============ CONVERSATION LIST ============
  const renderConversationList = () => (
    <div style={{
      width: isMobile ? '100%' : '320px',
      flexShrink: 0,
      borderRight: '1px solid #2A5470',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: '#0F3554'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #2A5470',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '18px', fontWeight: 600 }}>
          Team Chat
        </h3>
        <button
          onClick={() => setShowNewChat(true)}
          aria-label="New conversation"
          style={{
            backgroundColor: '#1E90FF',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#FFFFFF'
          }}
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Conversation list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: '40px 16px', textAlign: 'center', color: '#87CEEB' }}>
            Loading conversations...
          </div>
        ) : conversations.length === 0 ? (
          <div style={{ padding: '40px 16px', textAlign: 'center' }}>
            <Users size={48} style={{ color: '#4A7A94', marginBottom: '12px' }} />
            <p style={{ color: '#87CEEB', fontSize: '14px', margin: 0 }}>
              No team conversations yet
            </p>
            <p style={{ color: '#6CA6C1', fontSize: '12px', marginTop: '8px' }}>
              Click + to start a new conversation
            </p>
          </div>
        ) : conversations.map(convo => {
          const isActive = activeConversation?.id === convo.id;
          const unread = convo.unreadCount[currentUserId] || 0;
          const displayName = getConversationDisplayName(convo);

          return (
            <div
              key={convo.id}
              onClick={() => {
                setActiveConversation(convo);
                if (isMobile) setShowNewChat(false);
              }}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                backgroundColor: isActive ? '#1A4D6F' : 'transparent',
                borderLeft: isActive ? '3px solid #1E90FF' : '3px solid transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#142F47'; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {/* Avatar */}
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: convo.type === 'group' ? '#7C3AED' : '#1E90FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: '14px',
                fontWeight: 600,
                color: '#FFFFFF'
              }}>
                {convo.type === 'group' ? <Hash size={18} /> : getInitials(displayName)}
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: unread > 0 ? 700 : 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {displayName}
                  </span>
                  {convo.lastMessage && (
                    <span style={{ color: '#6CA6C1', fontSize: '11px', flexShrink: 0, marginLeft: '8px' }}>
                      {formatTime(convo.lastMessage.timestamp)}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2px' }}>
                  <span style={{
                    color: unread > 0 ? '#B0C4DE' : '#6CA6C1',
                    fontSize: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {convo.lastMessage
                      ? `${convo.lastMessage.senderId === currentUserId ? 'You: ' : ''}${convo.lastMessage.content}`
                      : 'No messages yet'}
                  </span>
                  {unread > 0 && (
                    <span style={{
                      backgroundColor: '#1E90FF',
                      color: '#FFFFFF',
                      borderRadius: '10px',
                      padding: '1px 7px',
                      fontSize: '11px',
                      fontWeight: 700,
                      flexShrink: 0,
                      marginLeft: '8px'
                    }}>
                      {unread}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ============ MESSAGE THREAD ============
  const renderMessageThread = () => {
    if (!activeConversation) {
      return (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6CA6C1',
          backgroundColor: '#0A2540'
        }}>
          <MessageCircle size={64} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <p style={{ fontSize: '16px', margin: 0 }}>Select a conversation or start a new one</p>
          <p style={{ fontSize: '13px', marginTop: '8px', color: '#4A7A94' }}>
            Your team messages are encrypted and HIPAA-compliant
          </p>
        </div>
      );
    }

    const displayName = getConversationDisplayName(activeConversation);
    const memberCount = activeConversation.members.length;

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#0A2540', height: '100%' }}>
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #2A5470',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          backgroundColor: '#0F3554'
        }}>
          {isMobile && (
            <button
              onClick={() => setActiveConversation(null)}
              style={{ background: 'none', border: 'none', color: '#87CEEB', cursor: 'pointer', padding: '4px' }}
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: activeConversation.type === 'group' ? '#7C3AED' : '#1E90FF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: 600
          }}>
            {activeConversation.type === 'group' ? <Hash size={16} /> : getInitials(displayName)}
          </div>
          <div>
            <div style={{ color: '#FFFFFF', fontSize: '15px', fontWeight: 600 }}>{displayName}</div>
            <div style={{ color: '#6CA6C1', fontSize: '12px' }}>
              {activeConversation.type === 'group'
                ? `${memberCount} members`
                : activeConversation.memberRoles[activeConversation.members.find(u => u !== currentUserId) || '']
                  ? getRoleLabel(activeConversation.memberRoles[activeConversation.members.find(u => u !== currentUserId) || ''])
                  : 'Team Member'}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#4A7A94', marginTop: '40px' }}>
              <p>Start of your conversation</p>
            </div>
          ) : (
            <>
              {messages.map((msg, i) => {
                const isOwn = msg.senderId === currentUserId;
                const showDateSep = i === 0 || messages[i - 1].timestamp.toDateString() !== msg.timestamp.toDateString();
                const showSender = !isOwn && activeConversation.type === 'group' &&
                  (i === 0 || messages[i - 1].senderId !== msg.senderId);

                return (
                  <React.Fragment key={msg.id}>
                    {showDateSep && (
                      <div style={{
                        textAlign: 'center',
                        margin: '16px 0',
                        color: '#6CA6C1',
                        fontSize: '12px'
                      }}>
                        <span style={{
                          backgroundColor: '#1A4D6F',
                          padding: '4px 12px',
                          borderRadius: '10px'
                        }}>
                          {msg.timestamp.toDateString() === new Date().toDateString() ? 'Today' :
                            msg.timestamp.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    )}
                    {showSender && (
                      <div style={{
                        marginBottom: '2px',
                        marginLeft: '48px',
                        fontSize: '12px',
                        color: '#87CEEB',
                        fontWeight: 600
                      }}>
                        {msg.senderName}
                        <span style={{
                          marginLeft: '6px',
                          fontSize: '10px',
                          color: getRoleBadgeColor(msg.senderRole),
                          fontWeight: 500
                        }}>
                          {getRoleLabel(msg.senderRole)}
                        </span>
                      </div>
                    )}
                    <div style={{
                      display: 'flex',
                      justifyContent: isOwn ? 'flex-end' : 'flex-start',
                      marginBottom: '6px',
                      gap: '8px'
                    }}>
                      {!isOwn && (
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: '#1A4D6F',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#87CEEB',
                          fontSize: '11px',
                          fontWeight: 600,
                          flexShrink: 0,
                          visibility: showSender || i === 0 || messages[i - 1].senderId !== msg.senderId ? 'visible' : 'hidden'
                        }}>
                          {getInitials(msg.senderName)}
                        </div>
                      )}
                      <div style={{
                        maxWidth: '70%',
                        padding: '8px 14px',
                        borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        backgroundColor: isOwn ? '#1E90FF' : '#1A4D6F',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        wordBreak: 'break-word'
                      }}>
                        {msg.isDeleted ? (
                          <span style={{ fontStyle: 'italic', opacity: 0.6 }}>[Message deleted]</span>
                        ) : (
                          <>
                            {msg.content}
                            {msg.isEdited && (
                              <span style={{ fontSize: '10px', opacity: 0.6, marginLeft: '6px' }}>(edited)</span>
                            )}
                          </>
                        )}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          gap: '4px',
                          marginTop: '4px'
                        }}>
                          <span style={{ fontSize: '10px', opacity: 0.6 }}>
                            {formatMessageTime(msg.timestamp)}
                          </span>
                          {isOwn && (
                            msg.readBy.length > 1
                              ? <CheckCheck size={12} style={{ color: '#87CEEB' }} />
                              : <Check size={12} style={{ opacity: 0.5 }} />
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              {typingUsers.length > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 0',
                  color: '#6CA6C1',
                  fontSize: '13px'
                }}>
                  <span>{typingUsers.map(u => u.userName).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...</span>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #2A5470',
          backgroundColor: '#0F3554',
          display: 'flex',
          alignItems: 'flex-end',
          gap: '8px'
        }}>
          <textarea
            ref={textareaRef}
            value={messageText}
            onChange={(e) => { setMessageText(e.target.value); handleTyping(); }}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            style={{
              flex: 1,
              padding: '10px 14px',
              backgroundColor: '#0A2540',
              border: '1px solid #2A5470',
              borderRadius: '20px',
              color: '#FFFFFF',
              fontSize: '14px',
              resize: 'none',
              outline: 'none',
              maxHeight: '120px',
              lineHeight: '1.4'
            }}
          />
          <button
            onClick={handleSend}
            disabled={!messageText.trim()}
            aria-label="Send message"
            style={{
              backgroundColor: messageText.trim() ? '#1E90FF' : '#2A5470',
              border: 'none',
              borderRadius: '50%',
              width: '38px',
              height: '38px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: messageText.trim() ? 'pointer' : 'not-allowed',
              color: '#FFFFFF',
              flexShrink: 0
            }}
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    );
  };

  // ============ NEW CONVERSATION MODAL ============
  const renderNewChatModal = () => {
    if (!showNewChat) return null;

    return (
      <NewChatModal
        currentUserId={currentUserId}
        currentUserName={getUserDisplayName()}
        currentUserRole={getUserRole()}
        onClose={() => setShowNewChat(false)}
        onCreated={(convo) => {
          setActiveConversation(convo);
          setShowNewChat(false);
        }}
      />
    );
  };

  // ============ MAIN RENDER ============
  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Show list on desktop always, on mobile only when no active convo */}
      {(!isMobile || !activeConversation) && renderConversationList()}
      {/* Show thread on desktop always, on mobile only when active convo */}
      {(!isMobile || activeConversation) && renderMessageThread()}
      {renderNewChatModal()}
    </div>
  );
};

// ============ NEW CHAT MODAL ============
interface NewChatModalProps {
  currentUserId: string;
  currentUserName: string;
  currentUserRole: string;
  onClose: () => void;
  onCreated: (convo: TeamConversation) => void;
}

const NewChatModal: React.FC<NewChatModalProps> = ({
  currentUserId,
  currentUserName,
  currentUserRole,
  onClose,
  onCreated
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<TeamMember[]>([]);
  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  const allMembers = TeamMemberService.getInstance().getAllTeamMembers()
    .filter(m => m.authUid && m.authUid !== currentUserId && m.status === 'active');

  const filteredMembers = allMembers.filter(m =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleMember = (member: TeamMember) => {
    setSelectedMembers(prev =>
      prev.find(m => m.id === member.id)
        ? prev.filter(m => m.id !== member.id)
        : [...prev, member]
    );
  };

  const handleCreate = async () => {
    if (selectedMembers.length === 0) return;
    setCreating(true);

    try {
      const isGroup = selectedMembers.length > 1;
      const members = [
        { uid: currentUserId, name: currentUserName, role: currentUserRole },
        ...selectedMembers.map(m => ({ uid: m.authUid!, name: m.name, role: m.role }))
      ];

      const name = isGroup
        ? (groupName.trim() || members.map(m => m.name.split(' ')[0]).join(', '))
        : '';

      const convoId = await firebaseTeamMessagingService.createConversation(
        members,
        name,
        currentUserId,
        isGroup ? 'group' : 'direct'
      );

      const convo = await firebaseTeamMessagingService.getConversation(convoId);
      if (convo) onCreated(convo);
    } catch (error) {
      console.error('Error creating conversation:', error);
    } finally {
      setCreating(false);
    }
  };

  const getRoleBadgeColor = (role: string): string => {
    switch (role) {
      case 'system_admin': return '#DC2626';
      case 'clinical_director': return '#7C3AED';
      case 'provider': return '#059669';
      case 'administrative_staff': return '#2563EB';
      default: return '#6B7280';
    }
  };

  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'system_admin': return 'Admin';
      case 'clinical_director': return 'Director';
      case 'provider': return 'Provider';
      case 'administrative_staff': return 'Staff';
      default: return role;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        backgroundColor: '#0F3554',
        borderRadius: '12px',
        width: '440px',
        maxWidth: '95vw',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #2A5470'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #2A5470',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '18px' }}>New Conversation</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#6CA6C1', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Selected Members */}
        {selectedMembers.length > 0 && (
          <div style={{
            padding: '8px 20px',
            borderBottom: '1px solid #2A5470',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px'
          }}>
            {selectedMembers.map(m => (
              <span
                key={m.id}
                style={{
                  backgroundColor: '#1A4D6F',
                  color: '#FFFFFF',
                  padding: '4px 10px',
                  borderRadius: '14px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {m.name}
                <X
                  size={12}
                  style={{ cursor: 'pointer', opacity: 0.7 }}
                  onClick={() => toggleMember(m)}
                />
              </span>
            ))}
          </div>
        )}

        {/* Group name (when multiple selected) */}
        {selectedMembers.length > 1 && (
          <div style={{ padding: '8px 20px', borderBottom: '1px solid #2A5470' }}>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Group name (optional)"
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#0A2540',
                border: '1px solid #2A5470',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
        )}

        {/* Search */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #2A5470' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6CA6C1' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search team members..."
              autoFocus
              style={{
                width: '100%',
                padding: '8px 12px 8px 34px',
                backgroundColor: '#0A2540',
                border: '1px solid #2A5470',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
        </div>

        {/* Member list */}
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: '300px' }}>
          {filteredMembers.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#6CA6C1', fontSize: '14px' }}>
              {allMembers.length === 0 ? 'No other team members found' : 'No matches'}
            </div>
          ) : filteredMembers.map(member => {
            const isSelected = selectedMembers.some(m => m.id === member.id);
            return (
              <div
                key={member.id}
                onClick={() => toggleMember(member)}
                style={{
                  padding: '10px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? '#1A4D6F' : 'transparent',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#142F47'; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {/* Checkbox */}
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  border: isSelected ? 'none' : '2px solid #4A7A94',
                  backgroundColor: isSelected ? '#1E90FF' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {isSelected && <Check size={14} color="#FFFFFF" />}
                </div>

                {/* Avatar */}
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: '#1A4D6F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#87CEEB',
                  fontSize: '13px',
                  fontWeight: 600,
                  flexShrink: 0
                }}>
                  {member.name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2)}
                </div>

                {/* Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 500 }}>{member.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                    <span style={{
                      fontSize: '11px',
                      color: getRoleBadgeColor(member.role),
                      fontWeight: 600
                    }}>
                      {getRoleLabel(member.role)}
                    </span>
                    <span style={{ color: '#6CA6C1', fontSize: '11px' }}>{member.email}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Action */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid #2A5470',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              backgroundColor: 'transparent',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              color: '#FFFFFF',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={selectedMembers.length === 0 || creating}
            style={{
              padding: '8px 20px',
              backgroundColor: selectedMembers.length > 0 ? '#1E90FF' : '#2A5470',
              border: 'none',
              borderRadius: '6px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: 600,
              cursor: selectedMembers.length > 0 ? 'pointer' : 'not-allowed',
              opacity: creating ? 0.7 : 1
            }}
          >
            {creating ? 'Creating...' : selectedMembers.length > 1 ? 'Create Group' : 'Start Chat'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamChatTab;
